import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { getIntl } from '../../../../../utils/intl';
import ContactDetailsForm from '../../../../shared/ContactDetailsForm';
import {
    CreateEmergencyContactDetailsBody,
    EmergencyContactDetails,
} from '../../../../../primary-record-types/apiTypes/medicalProfileService';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';

const intl = getIntl('emergencyContactDetailsCreate', {
    submit: 'Create Emergency Contact',
    cancel: 'Cancel',
    addEmergencyContact: 'Add Emergency Contact',
});

export type EmergencyContactCreateScreenOwnProps = {};

export type EmergencyContactCreateScreenProps = EmergencyContactCreateScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'create-emergency-contact'>;
};

type EmergencyContactDetailsInput = Omit<EmergencyContactDetails, 'contactDetailsId' | 'sourceEntityId'>;

const defaultEmergencyContactDetails: EmergencyContactDetailsInput = {
    entityType: 'medical-profile',
    contactType: 'emergency-contact',
    firstName: null,
    lastName: null,
    relationship: null,
    email: null,
    phoneNumber: null,
};

const EmergencyContactCreateScreen: React.FC<EmergencyContactCreateScreenProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const navigation = useProfilesTabNavigation();
    const { isLoadingProfile, createEmergencyContact, isUpdatingContact } = useMedicalProfile({
        medicalProfileId: props.route.params.profileId,
        onCreateEmergencyContactSuccess: () => {
            if (isMobile) {
                navigation.navigate('profile', {
                    profileId: props.route.params.profileId,
                });
            } else {
                navigation.navigate('profile-activity', {
                    profileId: props.route.params.profileId,
                });
            }
        },
    });

    return (
        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
            <AnimateLoadedContent isLoading={isUpdatingContact || isLoadingProfile}>
                {() => (
                    <ContactDetailsForm
                        title={intl('addEmergencyContact')}
                        key={props.route.params.profileId}
                        isDisabled={isUpdatingContact}
                        isSubmitting={isUpdatingContact}
                        initialValues={defaultEmergencyContactDetails}
                        fields={['firstName', 'lastName', 'relationship', 'email', 'phoneNumber']}
                        required={['firstName', 'lastName']}
                        submitButtonTitle={intl('submit')}
                        onSubmit={(values) => {
                            createEmergencyContact({
                                emergencyContactDetails: values as CreateEmergencyContactDetailsBody,
                                medicalProfileId: props.route.params.profileId,
                            });
                        }}
                    />
                )}
            </AnimateLoadedContent>
        </ProfileHeaderContainer>
    );
};

export default React.memo(EmergencyContactCreateScreen);
